import React from 'react';
import { DeliveryStop } from '../../../../services/deliveriesApi';
import { formatOrderId } from '../../../../utils/formatId';
import styles from './StopDetailsModal.module.css';

interface StopDetailsModalProps {
  open: boolean;
  stop: DeliveryStop | null;
  onClose: () => void;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
}

/**
 * Read-only details for a delivered (or failed) stop — POD photo, signature,
 * received-by, notes, timestamps, etc. Opened from the Routes page when the
 * driver hits "View Details" on a non-active stop.
 */
export const StopDetailsModal: React.FC<StopDetailsModalProps> = ({ open, stop, onClose }) => {
  if (!open || !stop) return null;

  const isDelivered = stop.status === 'delivered';
  const isFailed = stop.status === 'failed';

  return (
    <div className={styles.overlay} onClick={onClose} role="dialog" aria-modal="true">
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.header}>
          <div>
            <h2 className={styles.title}>
              Stop #{stop.sequence} — {stop.customer_name || `Order ${stop.order_id}`}
            </h2>
            <p className={styles.subtitle}>
              {formatOrderId(stop.order_id)}
            </p>
          </div>
          <button className={styles.close} onClick={onClose} aria-label="Close">×</button>
        </header>

        <div className={styles.body}>
          <span className={`${styles.statusPill} ${styles[`status_${stop.status}`]}`}>
            {stop.status.toUpperCase()}
          </span>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Customer</h3>
            <dl className={styles.kv}>
              <dt>Name</dt><dd>{stop.customer_name || '—'}</dd>
              <dt>Address</dt><dd>{stop.customer_address || '—'}</dd>
              <dt>Phone</dt><dd>{stop.customer_phone || '—'}</dd>
            </dl>
          </section>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Order</h3>
            <dl className={styles.kv}>
              <dt>Order #</dt><dd>{formatOrderId(stop.order_id)}</dd>
              <dt>Total</dt><dd>${Number(stop.order_total).toFixed(2)}</dd>
              <dt>Scheduled</dt>
              <dd>
                {stop.scheduled_window_start
                  ? `${formatDateTime(stop.scheduled_window_start)}${stop.scheduled_window_end ? ' – ' + formatDateTime(stop.scheduled_window_end) : ''}`
                  : '—'}
              </dd>
            </dl>
          </section>

          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Delivery</h3>
            <dl className={styles.kv}>
              <dt>Attempted</dt><dd>{formatDateTime(stop.attempted_at)}</dd>
              <dt>Completed</dt><dd>{formatDateTime(stop.completed_at)}</dd>
              <dt>Received by</dt><dd>{stop.pod_received_by || '—'}</dd>
              {stop.notes && <><dt>Notes</dt><dd className={styles.preWrap}>{stop.notes}</dd></>}
            </dl>
          </section>

          {isDelivered && (
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>Proof of Delivery</h3>
              <div className={styles.podGrid}>
                <div className={styles.podCol}>
                  <p className={styles.podLabel}>Photo</p>
                  {stop.pod_photo_url
                    ? <img src={stop.pod_photo_url} alt="Delivery photo" className={styles.podImg} />
                    : <p className={styles.muted}>Not captured</p>}
                </div>
                <div className={styles.podCol}>
                  <p className={styles.podLabel}>Signature</p>
                  {stop.pod_signature_url
                    ? <img src={stop.pod_signature_url} alt="Customer signature" className={styles.podImg} />
                    : <p className={styles.muted}>Not captured</p>}
                </div>
              </div>
            </section>
          )}

          {isFailed && (
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>Failure</h3>
              <dl className={styles.kv}>
                <dt>Reason</dt><dd>{stop.failure_reason || '—'}</dd>
                {stop.failure_notes && <><dt>Notes</dt><dd className={styles.preWrap}>{stop.failure_notes}</dd></>}
              </dl>
            </section>
          )}
        </div>

        <footer className={styles.footer}>
          <button type="button" className={styles.closeBtn} onClick={onClose}>Close</button>
        </footer>
      </div>
    </div>
  );
};

export default StopDetailsModal;
